<script setup lang="ts">
import { ZhButton, ZhEventLog, type ZhEventLogLine, ZhPanel, ZhSystemLabel } from '@zerohuman/ui'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { type AssemblyEvent, type AssemblyView, api } from '@/api'

/**
 * The assembly log both setup paths show (#1743): the setup chat at `assembly` and guided screen 5, above its tool
 * rows. Every line is an event the server recorded once the step it reports had committed; nothing here writes one.
 *
 * - It reads the server's event read by cursor, all of it first, then only what is after the newest event it holds,
 *   every `POLL_MS` while the work is running. Time passing with no new event adds no line.
 * - It stops asking once the assembly has finished (complete, partial or failed).
 * - A teammate whose automatic retries ran out offers Retry on its last failed line, which asks the server to set up
 *   that teammate only, then follows the log again.
 * - While the server reports the work running, a status under the log says it is under way, how many teammates are
 *   ready of how many, and that the tab can be closed. It is not a line and no timer changes it.
 */
const POLL_MS = 1500

const props = defineProps<{ assemblyId: string }>()
/** `finished`: the work stopped running (complete, partial or failed), so setup's step may have moved on. */
const emit = defineEmits<{ finished: [status: AssemblyView['status']] }>()

const events = ref<AssemblyEvent[]>([])
const status = ref<AssemblyView['status']>('running')
const retryable = ref<string[]>([])
const cursor = ref<number | undefined>(undefined)
const retrying = ref<string | null>(null)
const failure = ref<string | null>(null)
const ready = ref(0)
const total = ref(0)
const heard = ref(false)
let timer: ReturnType<typeof setTimeout> | null = null
let unmounted = false

const lines = computed<ZhEventLogLine[]>(() =>
    events.value.map(event => ({ id: event.seq, text: event.text, tone: event.tone })),
)

/**
 * While the server reports the work running, where it stands, from its own read: the panel is never a still title
 * between lines. It is a status, not a log line: no event is made up, and it goes once the work stops running.
 */
const progress = computed(() => {
    const where =
        heard.value && total.value > 0
            ? `Setting up your team: ${ready.value} of ${total.value} teammates ready so far.`
            : 'Setting up your team now.'
    return `${where} You can close this tab: setup carries on without it, and we’ll email you once your team is online.`
})

/** The teammate to retry from each line that offers Retry: the last failed line of each retryable teammate. */
const retryAt = computed(() => {
    const at = new Map<number, string>()
    for (const name of retryable.value) {
        const last = [...events.value].reverse().find(event => event.tone === 'failed' && event.teammate === name)
        if (last) {
            at.set(last.seq, name)
        }
    }
    return at
})

/** Takes in a read: the events it has not shown yet, in order, and where the work stands. */
function apply(view: AssemblyView): void {
    const shown = new Set(events.value.map(event => event.seq))
    const fresh = view.events.filter(event => !shown.has(event.seq))
    if (fresh.length > 0) {
        events.value = [...events.value, ...fresh].sort((a, b) => a.seq - b.seq)
    }
    cursor.value = Math.max(cursor.value ?? 0, view.cursor)
    const was = status.value
    status.value = view.status
    retryable.value = view.retryable
    ready.value = view.ready
    total.value = view.total
    heard.value = true
    if (was === 'running' && view.status !== 'running') {
        emit('finished', view.status)
    }
}

function schedule(): void {
    if (timer !== null) {
        clearTimeout(timer)
        timer = null
    }
    if (!unmounted && status.value === 'running') {
        timer = setTimeout(() => void read(), POLL_MS)
    }
}

async function read(): Promise<void> {
    timer = null
    try {
        const view = await api.assemblyEvents(props.assemblyId, cursor.value)
        if (unmounted) {
            return
        }
        apply(view)
        failure.value = null
    } catch {
        failure.value = 'Could not read your team’s setup just now. Trying again.'
    }
    schedule()
}

async function retry(teammate: string): Promise<void> {
    retrying.value = teammate
    failure.value = null
    try {
        apply(await api.assemblyRetry(props.assemblyId, teammate))
    } catch (error) {
        failure.value = error instanceof Error ? error.message : `Could not set up ${teammate} again.`
    } finally {
        retrying.value = null
    }
    schedule()
}

onMounted(() => {
    void read()
})

onBeforeUnmount(() => {
    unmounted = true
    if (timer !== null) {
        clearTimeout(timer)
        timer = null
    }
})
</script>

<template>
    <ZhPanel class="space-y-3" data-testid="assembly-log">
        <ZhSystemLabel tone="muted">Assembling your team</ZhSystemLabel>
        <ZhEventLog :lines="lines" label="Assembling your team">
            <template #action="{ line }">
                <ZhButton
                    v-if="retryAt.get(Number(line.id))"
                    variant="ghost"
                    data-testid="assembly-retry"
                    :loading="retrying === retryAt.get(Number(line.id))"
                    loading-label="RETRYING..."
                    @click="retry(retryAt.get(Number(line.id)) ?? '')"
                >
                    Retry
                </ZhButton>
            </template>
        </ZhEventLog>
        <p v-if="status === 'running'" role="status" class="text-sm" data-testid="assembly-log-progress">
            {{ progress }}
        </p>
        <p v-if="failure" role="alert" class="text-sm" data-testid="assembly-log-failure">{{ failure }}</p>
    </ZhPanel>
</template>
